import React from "react";
import { FullSkillData, Prof, Skill, SkillRowCount, SkillTree, getAvailablePointsAtLevel, getRequiredLevelForRow, getRequiredPointsForRow, getUsedSkillPoints } from "./SkillData";
import { SkillRow } from "./SkillRow";

import "./SkillSimulator.scss";
import { SkillPreviewPane } from "./SkillPreviewPane";
import { SkillSimulatorControls } from "./SkillSimulatorControls";
import { isSpecialSkill, MasterySkill, SkillMastery, test } from "./SkillMastery";
import { DndContext } from "@dnd-kit/core";

let globalSkillData: FullSkillData | null = null;

export function loadGlobalSkillData(skills: FullSkillData) {
    globalSkillData = skills;
}

export function getGlobalSkillData() {
    return globalSkillData;
}

export type SkillLevels = Record<number, number>;

export interface SkillSimulatorState {
    selectedSkill: number;
    masteryOpen: boolean;
}

export interface SkillSimulatorProps {
    prof: Prof;
    lvl: number;
    skillLevels: SkillLevels;
    masterySkills: MasterySkill[];
    masteryRepeat: boolean;
    // onChange?: (lvl: number, prof: Prof, levels: SkillLevels) => void;
    onChange?: (levels: SkillLevels) => void;
    onMasterySkillsChange?: (skills: MasterySkill[]) => void;
    onMasteryRepeatChange?: (repeat: boolean) => void;
}

function skillLevelsIdentical(a: SkillLevels, b: SkillLevels) {
    if (Object.keys(a).length != Object.keys(b).length) return false;
    for (const id in a) {
        if (b[id] != a[id]) return false;
    }
    return true;
}

let masterySkillID = 1;
export function getUniqueMasterySkillID() {
    return masterySkillID++;
}

export class SkillSimulator extends React.Component<SkillSimulatorProps, SkillSimulatorState> {
    constructor(props: SkillSimulatorProps) {
        super(props);
        this.state = {
            selectedSkill: -1,
            masteryOpen: false
        }
        if (globalSkillData == null) {
            throw new Error("skills must be loaded with loadGlobalSkillData before creating a SkillSimulator");
        }
        this.onSkillLevelChange = this.onSkillLevelChange.bind(this);
        this.onSkillSelected = this.onSkillSelected.bind(this);
        this.onSkillMasteryAddRequest = this.onSkillMasteryAddRequest.bind(this);
        this.onMasterySkillsChange = this.onMasterySkillsChange.bind(this);
        this.onMasteryRepeatChange = this.onMasteryRepeatChange.bind(this);
        this.toggleMastery = this.toggleMastery.bind(this);
        // this.onLvlChange = this.onLvlChange.bind(this);
        // this.onProfChange = this.onProfChange.bind(this);
    }
    componentDidUpdate(prevProps: Readonly<SkillSimulatorProps>, prevState: Readonly<SkillSimulatorState>, snapshot?: any): void {
        if (prevProps.lvl != this.props.lvl || prevProps.prof != this.props.prof) {
            const legalLevels = this.getLegalSkillLevels(this.props.skillLevels, this.props.lvl, this.props.prof);
            if (!skillLevelsIdentical(legalLevels, this.props.skillLevels)) {
                this.props.onChange?.(legalLevels);
                const legalMastery = this.getLegalMastery(legalLevels);
                if (legalMastery.length != this.props.masterySkills.length) {
                    this.props.onMasterySkillsChange?.(legalMastery);
                }
            }
        }
    }
    getSkillRows(prof?: Prof) {
        return globalSkillData!.skillTrees[prof ?? this.props.prof];
    }
    onSkillSelected(skillID: number) {
        this.setState({
            selectedSkill: skillID
        });
    }
    onSkillMasteryAddRequest(skillID: number) {
        if (this.props.masterySkills.length >= 20) return;

        const mastery = [...this.props.masterySkills];
        mastery.push({
            id: getUniqueMasterySkillID(),
            skill: skillID
        });
        this.props.onMasterySkillsChange?.(mastery);
    }
    onMasterySkillsChange(mastery: MasterySkill[]) {
        this.props?.onMasterySkillsChange?.(mastery);
    }
    onMasteryRepeatChange(repeat: boolean) {
        this.props.onMasteryRepeatChange?.(repeat);
    }
    getLegalMastery(levels: SkillLevels) {
        const legalEntries: MasterySkill[] = [];
        for (const masteryEntry of this.props.masterySkills) {
            if (levels[masteryEntry.skill] || isSpecialSkill(masteryEntry.skill)) {
                legalEntries.push(masteryEntry);
            }
        }
        return legalEntries;
    }
    getLegalSkillLevels(levels: SkillLevels, lvl: number, prof: Prof) {
        const allowedLevels: SkillLevels = {};
        const skillRows = this.getSkillRows(prof);
        const availablePoints = getAvailablePointsAtLevel(lvl);
        let learnedCount = 0;
        let i = 0;
    outer:
        for (i=0; i<skillRows.length; ++i) {
            const row = skillRows[i];
            for (const id of row) {
                if (learnedCount < getRequiredPointsForRow(i) || learnedCount >= availablePoints) {
                    break outer;
                }
                const pts = Math.min(levels[id] ?? 0, availablePoints - learnedCount);
                learnedCount += pts;
                allowedLevels[id] = pts;
            }
        }
        return allowedLevels;
    }
    // onLvlChange(lvl: number) {
    //     const legalLevels = this.ensureSkillLevelsLegal(this.props.skillLevels, lvl, this.props.prof);
    //     this.props.onChange?.(legalLevels);
    // }
    // onProfChange(prof: Prof) {
    //     const legalLevels = this.ensureSkillLevelsLegal(this.props.skillLevels, this.props.lvl, prof);
    //     this.props.onChange?.(legalLevels);
    // }
    onSkillLevelChange(skillID: number, level: number) {
        const levels = this.props.skillLevels;
        const currLevel = levels[skillID] ?? 0;
        const maxUpgrade = getAvailablePointsAtLevel(this.props.lvl) - getUsedSkillPoints(levels);
        const newLevel = currLevel + (Math.min(maxUpgrade, level - currLevel));
        if (newLevel != currLevel) {
            levels[skillID] = level;
            const legalLevels = this.getLegalSkillLevels(levels, this.props.lvl, this.props.prof);
            this.props.onChange?.(legalLevels);
        }
    }
    renderSkillRows(skillRows: SkillTree) {
        const res: React.ReactNode[] = [];
        for (let i=0; i<SkillRowCount; ++i) {
            const requiredLvl = getRequiredLevelForRow(i);
            const requiredPtsString = i > 0 ? ` i ${getRequiredPointsForRow(i)} pkt umiejętności` : "";
            res.push(<div key={SkillRowCount + i} className="skill-simulator-row-separator">Wymagania: <strong>{requiredLvl}</strong> poziom{requiredPtsString}</div>)
            res.push(<SkillRow onSkillSelected={this.onSkillSelected} onLevelChange={this.onSkillLevelChange} levels={this.props.skillLevels} 
                key={i} skills={globalSkillData!.skills} skillsIDs={skillRows[i]} selectedSkill={this.state.selectedSkill} 
                rowIndex={i} masteryOpen={this.state.masteryOpen} onSkillMasteryAddRequest={this.onSkillMasteryAddRequest}/>)
        }
        return res;
    }
    private toggleMastery() {
        this.setState({masteryOpen: !this.state.masteryOpen});
    }
    render(): React.ReactNode {
        const skillRows = this.getSkillRows();
        return <div className="skill-simulator-wrapper">
            {/* <SkillSimulatorControls lvl={this.state.lvl} prof={this.state.prof}
              onLvlChange={this.onLvlChange}
              onProfChange={this.onProfChange} /> */}
            <div className="skill-simulator">
                <div className="skill-simulator-skills">
                    {this.renderSkillRows(skillRows)}
                </div>
                {this.state.masteryOpen
                    ? <SkillMastery onCloseBtnClicked={this.toggleMastery} skills={this.props.masterySkills} repeat={this.props.masteryRepeat} 
                      onSkillsChange={this.onMasterySkillsChange} onRepeatChange={this.onMasteryRepeatChange} onSkillAddRequest={this.onSkillMasteryAddRequest} />
                    : <SkillPreviewPane lvl={this.props.lvl} selectedSkill={this.state.selectedSkill} levels={this.props.skillLevels} skills={globalSkillData!.skills} 
                    onLevelChange={this.onSkillLevelChange} skillTree={skillRows} onMasteryBtnClicked={this.toggleMastery} />
                }
            </div>
            <div onClick={this.toggleMastery} className="mastery-btn">Edytuj mistrzostwo walk</div>
        </div>
    }
}
